/**
 * DataForSEO API Client
 * Provides methods to interact with DataForSEO APIs for keyword research, SERP tracking, and more
 * Documentation: https://docs.dataforseo.com/v3/
 */

import axios, { type AxiosInstance, type AxiosError } from 'axios';

interface DataForSEOConfig {
  login: string;
  password: string;
  baseUrl?: string;
}

interface DataForSEOResponse<T = any> {
  version: string;
  status_code: number;
  status_message: string;
  time: string;
  cost: number;
  tasks_count: number;
  tasks_error: number;
  tasks: Array<{
    id: string;
    status_code: number;
    status_message: string;
    time: string;
    cost: number;
    result_count: number;
    path: string[];
    data: any;
    result: T[];
  }>;
}

interface KeywordData {
  keyword: string;
  location_code: number;
  language_code: string;
  search_partners: boolean;
  competition: number;
  competition_level: string;
  cpc: number;
  search_volume: number;
  low_top_of_page_bid: number;
  high_top_of_page_bid: number;
  categories: number[];
  monthly_searches: Array<{
    year: number;
    month: number;
    search_volume: number;
  }>;
}

interface KeywordSuggestion {
  keyword: string;
  location_code: number;
  language_code: string;
  search_volume: number;
  competition: number;
  cpc: number;
  categories: number[];
}

interface KeywordDifficulty {
  keyword: string;
  keyword_difficulty: number;
  search_intent: string;
}

export class DataForSEOClient {
  private client: AxiosInstance;
  private config: DataForSEOConfig;

  constructor(config: DataForSEOConfig) {
    this.config = {
      baseUrl: config.baseUrl || 'https://api.dataforseo.com',
      ...config,
    };

    // Ensure baseUrl is always set to prevent relative requests
    const { baseUrl: configuredBaseUrl, login, password } = this.config;
    const baseUrl = configuredBaseUrl || 'https://api.dataforseo.com';

    if (!login || !password) {
      console.error('DataForSEO: Missing login or password credentials');
      throw new Error('DataForSEO login and password are required');
    }

    // Create axios instance with basic auth
    // Include some debugging headers to help identify issues
    console.log('DataForSEO: Creating client with baseURL:', baseUrl, 'login:', login);
    this.client = axios.create({
      baseURL: baseUrl,
      headers: {
        'Authorization': 'Basic ' + btoa(login + ':' + password),
        'Content-Type': 'application/json',
        'User-Agent': 'RAHS-MARKETING/1.0',
      },
      timeout: 30000, // 30 second timeout
    });

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        const { config, response, message, isAxiosError } = error;

        console.error('DataForSEO API Error Details:', {
          url: config?.url,
          method: config?.method,
          status: response?.status,
          statusText: response?.statusText,
          data: response?.data,
          message,
          isAxiosError,
        });

        // Check if this is an auth/token issue
        if (response?.status === 401) {
          console.error('DataForSEO: Authentication failed. Check credentials.');
        } else if (response?.status === 405) {
          console.error('DataForSEO: Method not allowed. Check HTTP method and endpoint.');
        }

        throw error;
      }
    );
  }

  /**
   * Get search volume and metrics for keywords
   * Endpoint: POST /v3/keywords_data/google_ads/search_volume/live
   */
  async getKeywordSearchVolume(
    keywords: string[],
    locationCode: number = 2840, // USA
    languageCode: string = 'en'
  ): Promise<KeywordData[]> {
    const response = await this.client.post<DataForSEOResponse<KeywordData>>(
      '/v3/keywords_data/google_ads/search_volume/live',
      [
        {
          keywords,
          location_code: locationCode,
          language_code: languageCode,
          search_partners: false,
        },
      ]
    );

    // DataForSEO API returns results directly in tasks[0].result array
    if (response.data.tasks?.[0]?.result) {
      return response.data.tasks[0].result;
    }
    return [];
  }

  /**
   * Get keyword suggestions based on seed keywords
   * Endpoint: POST /v3/keywords_data/google_ads/keywords_for_keywords/live
   */
  async getKeywordSuggestions(
    keywords: string[],
    locationCode: number = 2840,
    languageCode: string = 'en',
    includeAdultKeywords: boolean = false
  ): Promise<KeywordSuggestion[]> {
    const response = await this.client.post<DataForSEOResponse<KeywordSuggestion>>(
      '/v3/keywords_data/google_ads/keywords_for_keywords/live',
      [
        {
          keywords,
          location_code: locationCode,
          language_code: languageCode,
          include_adult_keywords: includeAdultKeywords,
        },
      ]
    );

    if (response.data.tasks?.[0]?.result) {
      return response.data.tasks[0].result;
    }
    return [];
  }

  /**
   * Get keyword ideas (broader suggestions)
   * Endpoint: POST /v3/dataforseo_labs/google/keyword_ideas/live
   */
  async getKeywordIdeas(
    keywords: string[],
    locationCode: number = 2840,
    languageCode: string = 'en'
  ): Promise<any[]> {
    const response = await this.client.post(
      '/v3/dataforseo_labs/google/keyword_ideas/live',
      [
        {
          keywords,
          location_code: locationCode,
          language_code: languageCode,
          include_serp_info: true,
          include_seed_keyword: true,
          limit: 100,
        },
      ]
    );

    if (response.data.tasks?.[0]?.result) {
      return response.data.tasks[0].result;
    }
    return [];
  }

  /**
   * Get bulk keyword difficulty scores
   * Endpoint: POST /v3/dataforseo_labs/google/bulk_keyword_difficulty/live
   * Note: This endpoint does NOT support location_code parameter per DataForSEO v3 docs
   */
  async getKeywordDifficulty(
    keywords: string[],
    languageCode: string = 'en'
  ): Promise<KeywordDifficulty[]> {
    const response = await this.client.post<DataForSEOResponse<KeywordDifficulty>>(
      '/v3/dataforseo_labs/google/bulk_keyword_difficulty/live',
      [
        {
          keywords,
          language_code: languageCode,
        },
      ]
    );

    // DataForSEO API returns results directly in tasks[0].result array
    if (response.data.tasks?.[0]?.result) {
      return response.data.tasks[0].result;
    }
    return [];
  }

  /**
   * Get keywords for a specific website/domain
   * Endpoint: POST /v3/keywords_data/google_ads/keywords_for_site/live
   */
  async getKeywordsForSite(
    targetUrl: string,
    locationCode: number = 2840,
    languageCode: string = 'en'
  ): Promise<KeywordSuggestion[]> {
    const response = await this.client.post<DataForSEOResponse<KeywordSuggestion>>(
      '/v3/keywords_data/google_ads/keywords_for_site/live',
      [
        {
          target: targetUrl,
          location_code: locationCode,
          language_code: languageCode,
        },
      ]
    );

    if (response.data.tasks?.[0]?.result) {
      return response.data.tasks[0].result;
    }
    return [];
  }

  /**
   * Get SERP results for a keyword
   * Endpoint: POST /v3/serp/google/organic/live/regular
   */
  async getSerpResults(
    keyword: string,
    locationCode: number = 2840,
    languageCode: string = 'en',
    device: 'desktop' | 'mobile' = 'desktop'
  ): Promise<any[]> {
    const response = await this.client.post(
      '/v3/serp/google/organic/live/regular',
      [
        {
          keyword,
          location_code: locationCode,
          language_code: languageCode,
          device,
          os: device === 'mobile' ? 'ios' : undefined,
        },
      ]
    );

    if (response.data.tasks?.[0]?.result) {
      return response.data.tasks[0].result;
    }
    return [];
  }

  /**
   * Get autocomplete suggestions from Google
   * Endpoint: POST /v3/serp/google/autocomplete/live/advanced
   */
  async getAutocomplete(
    keyword: string,
    locationCode: number = 2840,
    languageCode: string = 'en'
  ): Promise<any[]> {
    const response = await this.client.post(
      '/v3/serp/google/autocomplete/live/advanced',
      [
        {
          keyword,
          location_code: locationCode,
          language_code: languageCode,
        },
      ]
    );

    if (response.data.tasks?.[0]?.result) {
      return response.data.tasks[0].result;
    }
    return [];
  }

  /**
   * Extract People Also Ask questions from SERP results
   * This is a helper method that extracts PAA from SERP data
   */
  extractPAAFromSerp(serpResults: any[]): any[] {
    const paaQuestions: any[] = [];
    
    for (const result of serpResults) {
      if (result.items) {
        for (const item of result.items) {
          // Look for 'people_also_ask' type items
          if (item.type === 'people_also_ask' && item.items) {
            for (const paaItem of item.items) {
              paaQuestions.push({
                question: paaItem.title || paaItem.question,
                answer_snippet: paaItem.snippet || paaItem.answer,
                source_url: paaItem.url,
                source_title: paaItem.source || paaItem.domain,
                xpath: paaItem.xpath,
              });
            }
          }
          // Also check for related_searches
          if (item.type === 'related_searches' && item.items) {
            // Store related searches separately or as part of PAA context
            for (const relatedItem of item.items) {
              if (relatedItem.type === 'related_searches_element') {
                paaQuestions.push({
                  question: relatedItem.title,
                  answer_snippet: null,
                  source_url: relatedItem.url,
                  source_title: 'Related Search',
                  xpath: relatedItem.xpath,
                  is_related_search: true,
                });
              }
            }
          }
        }
      }
    }
    
    return paaQuestions;
  }

  /**
   * Get comprehensive keyword expansion data (autocomplete + suggestions + PAA)
   * This is a convenience method that combines multiple data sources
   * Updated to match DataForSEO v3 API specifications
   */
  async getKeywordExpansion(
    keyword: string,
    locationCode: number = 2840,
    languageCode: string = 'en'
  ): Promise<{
    autocomplete: any[];
    suggestions: any[];
    paa: any[];
    keyword_ideas: any[];
  }> {
    try {
      // Run requests in parallel for efficiency
      const [autocompleteData, suggestionsData, serpData, keywordIdeasData] = await Promise.all([
        this.getAutocomplete(keyword, locationCode, languageCode),
        this.getKeywordSuggestions([keyword], locationCode, languageCode),
        this.getSerpResults(keyword, locationCode, languageCode),
        this.getKeywordIdeas([keyword], locationCode, languageCode).catch(() => []),
      ]);

      // Extract PAA from SERP results
      const paaData = this.extractPAAFromSerp(serpData);

      return {
        autocomplete: autocompleteData,
        suggestions: suggestionsData,
        paa: paaData,
        keyword_ideas: keywordIdeasData,
      };
    } catch (error) {
      console.error('Error getting keyword expansion:', error);
      return {
        autocomplete: [],
        suggestions: [],
        paa: [],
        keyword_ideas: [],
      };
    }
  }

  /**
   * Get SERP results with GPS coordinates for local search
   * Endpoint: POST /v3/serp/google/organic/live/advanced
   * Per DataForSEO v3 docs:
   * - location_coordinate format: "latitude,longitude"
   * - depth parameter controls number of results (max 100)
   */
  async getSerpWithCoordinates(
    keyword: string,
    latitude: number,
    longitude: number,
    languageCode: string = 'en',
    device: 'desktop' | 'mobile' = 'desktop'
  ): Promise<any[]> {
    const response = await this.client.post(
      '/v3/serp/google/organic/live/advanced',
      [
        {
          keyword,
          location_coordinate: `${latitude},${longitude}`,
          language_code: languageCode,
          device,
          os: device === 'mobile' ? 'ios' : undefined,
          depth: 100, // Get up to 100 results
        },
      ]
    );

    if (response.data.tasks?.[0]?.result) {
      return response.data.tasks[0].result;
    }
    return [];
  }

  /**
   * Get Local Pack results (Maps results in SERP)
   * Extracts local pack from SERP results
   */
  extractLocalPackFromSerp(serpResults: any[]): any[] {
    const localPackResults: any[] = [];
    
    for (const result of serpResults) {
      if (result.items) {
        for (const item of result.items) {
          if (item.type === 'local_pack' && item.items) {
            for (const localItem of item.items) {
              localPackResults.push({
                place_id: localItem.place_id,
                business_name: localItem.title,
                address: localItem.address,
                phone: localItem.phone,
                position: localItem.rank_absolute,
                rating: localItem.rating?.value,
                reviews_count: localItem.rating?.votes_count,
                category: localItem.category,
                latitude: localItem.latitude,
                longitude: localItem.longitude,
                distance: localItem.distance,
                url: localItem.url,
              });
            }
          }
        }
      }
    }
    
    return localPackResults;
  }

  /**
   * Get Maps results with GPS precision
   * Endpoint: POST /v3/serp/google/maps/live/advanced
   * Per DataForSEO v3 docs:
   * - location_coordinate format: "latitude,longitude"
   * - Returns map pack results with business listings
   */
  async getMapsResults(
    keyword: string,
    latitude: number,
    longitude: number,
    languageCode: string = 'en'
  ): Promise<any[]> {
    const response = await this.client.post(
      '/v3/serp/google/maps/live/advanced',
      [
        {
          keyword,
          location_coordinate: `${latitude},${longitude}`,
          language_code: languageCode,
        },
      ]
    );

    if (response.data.tasks?.[0]?.result) {
      return response.data.tasks[0].result;
    }
    return [];
  }

  /**
   * Track rankings for a specific domain
   * Searches SERP results for target domain and returns position
   */
  findDomainInSerp(serpResults: any[], targetDomain: string): {
    found: boolean;
    position: number | null;
    url: string | null;
    title: string | null;
    type: string | null;
  } {
    for (const result of serpResults) {
      if (result.items) {
        for (const item of result.items) {
          if (item.type === 'organic' && item.domain?.includes(targetDomain)) {
            return {
              found: true,
              position: item.rank_absolute,
              url: item.url,
              title: item.title,
              type: 'organic',
            };
          }
          // Check local pack
          if (item.type === 'local_pack' && item.items) {
            for (const localItem of item.items) {
              if (localItem.domain?.includes(targetDomain) || 
                  localItem.url?.includes(targetDomain)) {
                return {
                  found: true,
                  position: localItem.rank_absolute,
                  url: localItem.url,
                  title: localItem.title,
                  type: 'local_pack',
                };
              }
            }
          }
        }
      }
    }
    
    return {
      found: false,
      position: null,
      url: null,
      title: null,
      type: null,
    };
  }

  /**
   * Calculate CTR-weighted visibility score
   * Uses industry-standard CTR by position
   */
  calculateVisibilityScore(
    position: number,
    searchVolume: number,
    device: 'desktop' | 'mobile' = 'desktop'
  ): number {
    // Industry-standard CTR by position (Advanced Web Ranking data)
    const ctrCurve: { [key: number]: { desktop: number; mobile: number } } = {
      1: { desktop: 0.3945, mobile: 0.3126 },
      2: { desktop: 0.1855, mobile: 0.1549 },
      3: { desktop: 0.1105, mobile: 0.0991 },
      4: { desktop: 0.0745, mobile: 0.0715 },
      5: { desktop: 0.0559, mobile: 0.0547 },
      6: { desktop: 0.0453, mobile: 0.0445 },
      7: { desktop: 0.0386, mobile: 0.0380 },
      8: { desktop: 0.0335, mobile: 0.0330 },
      9: { desktop: 0.0296, mobile: 0.0292 },
      10: { desktop: 0.0264, mobile: 0.0261 },
    };

    const ctr = ctrCurve[position]?.[device] || 0.01; // Default 1% for positions > 10
    const estimatedClicks = searchVolume * ctr;
    const visibilityScore = estimatedClicks * 100; // Normalize to 0-100 scale

    return Math.round(visibilityScore);
  }

  /**
   * Track API usage for cost monitoring
   */
  async getApiUsage(): Promise<any> {
    try {
      const response = await this.client.get('/v3/appendix/user_data');
      return response.data;
    } catch (error) {
      console.error('Error fetching API usage:', error);
      return null;
    }
  }

  /**
   * Get available locations for targeting
   */
  async getLocations(): Promise<any[]> {
    try {
      const response = await this.client.get('/v3/keywords_data/google_ads/locations');
      if (response.data.tasks?.[0]?.result) {
        return response.data.tasks[0].result;
      }
      return [];
    } catch (error) {
      console.error('Error fetching locations:', error);
      return [];
    }
  }

  /**
   * Get available languages
   */
  async getLanguages(): Promise<any[]> {
    try {
      const response = await this.client.get('/v3/keywords_data/google_ads/languages');
      if (response.data.tasks?.[0]?.result) {
        return response.data.tasks[0].result;
      }
      return [];
    } catch (error) {
      console.error('Error fetching languages:', error);
      return [];
    }
  }

  /**
   * ============== BACKLINKS API METHODS ==============
   */

  /**
   * Get backlink summary for a domain
   */
  async getBacklinkSummary(target: string): Promise<any> {
    const response = await this.client.post('/v3/backlinks/summary/live', [{
      target,
      include_subdomains: true
    }]);

    if (response.data.tasks?.[0]?.result?.[0]) {
      return response.data.tasks[0].result[0];
    }
    return null;
  }

  /**
   * Get backlinks for a domain
   */
  async getBacklinks(target: string, limit: number = 100): Promise<any[]> {
    const response = await this.client.post('/v3/backlinks/backlinks/live', [{
      target,
      include_subdomains: true,
      limit
    }]);

    if (response.data.tasks?.[0]?.result?.[0]?.items) {
      return response.data.tasks[0].result[0].items;
    }
    return [];
  }

  /**
   * Get referring domains
   */
  async getReferringDomains(target: string, limit: number = 100): Promise<any[]> {
    const response = await this.client.post('/v3/backlinks/referring_domains/live', [{
      target,
      include_subdomains: true,
      limit
    }]);

    if (response.data.tasks?.[0]?.result?.[0]?.items) {
      return response.data.tasks[0].result[0].items;
    }
    return [];
  }

  /**
   * ============== ONPAGE API METHODS ==============
   */

  /**
   * Start OnPage crawl
   */
  async startOnPageCrawl(target: string, maxPages: number = 1000): Promise<string | null> {
    const response = await this.client.post('/v3/on_page/task_post', [{
      target,
      max_crawl_pages: maxPages,
      store_raw_html: false,
      enable_javascript: true
    }]);

    if (response.data.tasks?.[0]?.id) {
      return response.data.tasks[0].id;
    }
    return null;
  }

  /**
   * Get OnPage crawl summary
   */
  async getOnPageSummary(taskId: string): Promise<any> {
    const response = await this.client.get(`/v3/on_page/summary/${taskId}`);

    if (response.data.tasks?.[0]?.result?.[0]) {
      return response.data.tasks[0].result[0];
    }
    return null;
  }

  /**
   * Get OnPage crawl progress
   */
  async getOnPageTasksReady(): Promise<any[]> {
    const response = await this.client.get('/v3/on_page/tasks_ready');

    if (response.data.tasks) {
      return response.data.tasks;
    }
    return [];
  }

  /**
   * Get OnPage pages results
   */
  async getOnPagePages(taskId: string, limit: number = 100): Promise<any[]> {
    const response = await this.client.post('/v3/on_page/pages', [{
      id: taskId,
      filters: [],
      order_by: ['checks.failed.resource_broken', 'desc'],
      limit
    }]);

    if (response.data.tasks?.[0]?.result?.[0]?.items) {
      return response.data.tasks[0].result[0].items;
    }
    return [];
  }

  /**
   * Get OnPage non-indexable pages
   */
  async getOnPageNonIndexable(taskId: string): Promise<any[]> {
    const response = await this.client.post('/v3/on_page/non_indexable', [{
      id: taskId
    }]);

    if (response.data.tasks?.[0]?.result?.[0]?.items) {
      return response.data.tasks[0].result[0].items;
    }
    return [];
  }

  /**
   * ============== BUSINESS DATA API METHODS ==============
   */

  /**
   * Get Google Business Profile reviews
   */
  async getBusinessReviews(target: string, sortBy: string = 'newest'): Promise<any[]> {
    const response = await this.client.post('/v3/business_data/google/reviews/task_post', [{
      query: target,
      sort_by: sortBy,
      limit: 50
    }]);

    // For immediate results, wait or return IDs for later collection
    if (response.data.tasks?.[0]?.id) {
      // In a real implementation, you'd collect results later
      // For now, return the task ID
      return [{ taskId: response.data.tasks[0].id }];
    }
    return [];
  }

  /**
   * Get business listings search
   */
  async searchBusinessListings(query: string, location: string): Promise<any[]> {
    const response = await this.client.post('/v3/business_data/business_listings/search/live', [{
      query,
      location,
      limit: 50
    }]);

    if (response.data.tasks?.[0]?.result?.[0]?.items) {
      return response.data.tasks[0].result[0].items;
    }
    return [];
  }

  /**
   * ============== CONTENT ANALYSIS API METHODS ==============
   */

  /**
   * Get content mentions/summary
   */
  async getContentSummary(keyword: string): Promise<any> {
    const response = await this.client.post('/v3/content_analysis/summary/live', [{
      keyword
    }]);

    if (response.data.tasks?.[0]?.result) {
      return response.data.tasks[0];
    }
    return null;
  }

  /**
   * Get content search results
   */
  async getContentSearch(keyword: string, limit: number = 100): Promise<any[]> {
    const response = await this.client.post('/v3/content_analysis/search/live', [{
      keyword,
      order_by: ['published_time', 'desc'],
      limit
    }]);

    if (response.data.tasks?.[0]?.result) {
      return response.data.tasks[0].result;
    }
    return [];
  }

  /**
   * ============== CONTENT GENERATION API METHODS ==============
   */

  /**
   * Generate text content
   */
  async generateContent(topic: string, wordCount: number = 500): Promise<string> {
    const response = await this.client.post('/v3/content_generation/generate_text/live', [{
      topic,
      word_count: wordCount
    }]);

    if (response.data.tasks?.[0]?.result?.[0]?.text) {
      return response.data.tasks[0].result[0].text;
    }
    return '';
  }

  /**
   * Generate meta descriptions
   */
  async generateMetaTags(text: string): Promise<any> {
    const response = await this.client.post('/v3/content_generation/generate_meta_tags/live', [{
      text
    }]);

    if (response.data.tasks?.[0]?.result?.[0]) {
      return response.data.tasks[0].result[0];
    }
    return null;
  }

  /**
   * Check grammar and spelling
   */
  async checkGrammar(text: string): Promise<any[]> {
    const response = await this.client.post('/v3/content_generation/check_grammar/live', [{
      text,
      language: 'en'
    }]);

    if (response.data.tasks?.[0]?.result?.[0]?.items) {
      return response.data.tasks[0].result[0].items;
    }
    return [];
  }

  /**
   * ============== DOMAIN ANALYTICS API METHODS ==============
   */

  /**
   * Get domain Whois information
   */
  async getDomainWhois(domain: string): Promise<any> {
    const response = await this.client.post('/v3/domain_analytics/whois/overview/live', [{
      filters: [['domain', '=', domain]]
    }]);

    if (response.data.tasks?.[0]?.result?.[0]) {
      return response.data.tasks[0].result[0];
    }
    return null;
  }

  /**
   * Get technologies used on a domain
   */
  async getDomainTechnologies(domain: string): Promise<any[]> {
    const response = await this.client.post('/v3/domain_analytics/technologies/domain_technologies/live', [{
      target: domain
    }]);

    if (response.data.tasks?.[0]?.result) {
      return response.data.tasks[0].result;
    }
    return [];
  }

  /**
   * ============== DATABASE API METHODS ==============
   */

  /**
   * Get backlink summary database info
   */
  async getBacklinkDatabaseIndex(): Promise<any> {
    const response = await this.client.get('/v3/backlinks/index');

    if (response.data) {
      return response.data;
    }
    return null;
  }

  /**
   * ============== BATCH OPERATIONS ==============
   */

  /**
   * Bulk backlink analysis for multiple domains
   */
  async getBulkBacklinks(domains: string[]): Promise<any[]> {
    if (domains.length === 0) return [];

    const response = await this.client.post('/v3/backlinks/bulk_backlinks/live', [{
      targets: domains.slice(0, 100) // Limit to 100 domains
    }]);

    if (response.data.tasks?.[0]?.result) {
      return response.data.tasks[0].result;
    }
    return [];
  }

  /**
   * ============== ADVANCED INTELLIGENCE METHODS ==============
   */

  /**
   * Comprehensive domain intelligence analysis
   */
  async getComprehensiveDomainAnalysis(domain: string): Promise<{
    backlinks?: any;
    onpage?: any;
    technologies?: any[];
    whois?: any;
    keywords?: any[];
    serp_position?: any;
  }> {
    try {
      const [backlinks, onpage, technologies, whois, keywords, serp] = await Promise.allSettled([
        this.getBacklinkSummary(domain),
        this.startOnPageCrawl(`https://${domain}`, 100).then(id => id ? this.getOnPageSummary(id) : null),
        this.getDomainTechnologies(domain),
        this.getDomainWhois(domain),
        this.getKeywordsForSite(`https://${domain}`),
        this.getSerpResults(domain).then(results => this.findDomainInSerp(results, domain))
      ]);

      return {
        backlinks: backlinks.status === 'fulfilled' ? backlinks.value : null,
        onpage: onpage.status === 'fulfilled' ? onpage.value : null,
        technologies: technologies.status === 'fulfilled' ? technologies.value : [],
        whois: whois.status === 'fulfilled' ? whois.value : null,
        keywords: keywords.status === 'fulfilled' ? keywords.value : [],
        serp_position: serp.status === 'fulfilled' ? serp.value : null,
      };
    } catch (error) {
      console.error('Error in comprehensive domain analysis:', error);
      return {};
    }
  }

  /**
   * Brand intelligence gathering (mentions, backlinks, reviews)
   */
  async getBrandIntelligence(brand: string, domain?: string): Promise<{
    backlinks?: any;
    mentions?: any[];
    reviews?: any[];
    content_analysis?: any;
  }> {
    try {
      const [backlinks, mentions, content] = await Promise.allSettled([
        domain ? this.getBacklinkSummary(domain) : Promise.resolve(null),
        this.getContentSummary(brand),
        this.getContentSearch(brand, 20)
      ]);

      // Reviews would typically be domain-specific
      let reviews = [];
      if (domain) {
        try {
          reviews = await this.getBusinessReviews(domain);
        } catch (e) {
          // Reviews might not be available for all domains
        }
      }

      return {
        backlinks: backlinks.status === 'fulfilled' ? backlinks.value : null,
        mentions: content.status === 'fulfilled' ? content.value : [],
        reviews,
        content_analysis: mentions.status === 'fulfilled' ? mentions.value : null,
      };
    } catch (error) {
      console.error('Error in brand intelligence gathering:', error);
      return {};
    }
  }

  /**
   * Check API credentials
   */
  async testConnection(): Promise<boolean> {
    try {
      const response = await this.client.get('/v3/appendix/user_data');
      return response.status === 200;
    } catch (error) {
      return false;
    }
  }
}

/**
 * Create DataForSEO client instance from environment variables
 */
export function createDataForSEOClient(): DataForSEOClient | null {
  const login = import.meta.env.VITE_DATAFORSEO_LOGIN;
  const password = import.meta.env.VITE_DATAFORSEO_PASSWORD;

  if (!login || !password) {
    console.warn('DataForSEO credentials not configured. Set VITE_DATAFORSEO_LOGIN and VITE_DATAFORSEO_PASSWORD in .env');
    return null;
  }

  return new DataForSEOClient({
    login,
    password,
    baseUrl: import.meta.env.VITE_DATAFORSEO_API_URL,
  });
}

/**
 * Check if DataForSEO is configured
 */
export function isDataForSEOConfigured(): boolean {
  const login = import.meta.env.VITE_DATAFORSEO_LOGIN;
  const password = import.meta.env.VITE_DATAFORSEO_PASSWORD;
  return Boolean(login && password);
}

// Export types
export type {
  DataForSEOConfig,
  DataForSEOResponse,
  KeywordData,
  KeywordSuggestion,
  KeywordDifficulty,
};
